import { DatePipe } from '@angular/common';
import { NativeDateAdapter } from '@angular/material/core';

export interface DateDisplay {
    year: string;
    month: string;
    day: string;
}

export const CUSTOM_DATE_FORMATS = {
    parse: {
        dateInput: { month: 'short', year: 'numeric', day: 'numeric' },
    },
    display: {
        //dateInput: { month: 'short', year: 'numeric', day: 'numeric'},
        dateInput: 'customInput',
        monthYearLabel: { year: 'numeric', month: 'short' },
        dateA11yLabel: { year: 'numeric', month: 'long', day: 'numeric' },
        monthYearA11yLabel: { year: 'numeric', month: 'long' },
    },
};

export class CustomDatePickerAdapter extends NativeDateAdapter {
    parse(value: string | number): Date | null {
        if (typeof value === 'string') {
            // Leerzeichen am Anfang und Ende entfernen
            value = value.trim();
            let pattern: RegExp;

            if (this.locale === 'de-DE') {
                // Strikt: dd.MM.yyyy (genau 2 Stellen Tag, 2 Stellen Monat, 4 Stellen Jahr)
                pattern = /^(\d{2})\.(\d{2})\.(\d{4})$/;
            } else if (this.locale === 'en-GB') {
                // Strikt: dd/MM/yyyy (genau 2 Stellen Tag, 2 Stellen Monat, 4 Stellen Jahr)
                pattern = /^(\d{2})\/(\d{2})\/(\d{4})$/;
            } else {
                return null;
            }

            const match = value.match(pattern);

            if (match) {
                const day = Number(match[1]);
                const month = Number(match[2]);
                const year = Number(match[3]);

                // Basis-Validierung: Plausible Bereiche
                if (day < 1 || day > 31 || month < 1 || month > 12 || year < 1900 || year > 2100) {
                    return null;
                }

                // Prüfe ob Datum wirklich existiert (z.B. kein 31.02.2025)
                const date = new Date(year, month - 1, day);

                // JavaScript korrigiert automatisch ungültige Datumsangaben
                // Daher prüfen wir, ob das erstellte Datum noch mit der Eingabe übereinstimmt
                if (date.getDate() !== day ||
                    date.getMonth() !== month - 1 ||
                    date.getFullYear() !== year) {
                    return null; // Datum existiert nicht
                }

                return date;
            }

            return null; // Kein Match = ungültiges Format
        }

        const timestamp: number =
            typeof value === 'number' ? value : Date.parse(value);
        return isNaN(timestamp)
            ? null
            : this.toUtcDateFromTimestamp(new Date(timestamp));
    }

    format(date: Date, display: string | DateDisplay): string {
        if (display === 'customInput') {
            if (this.locale === 'de-DE') {
                return new DatePipe(this.locale).transform(date, 'dd.MM.yyyy');
            } else {
                return new DatePipe(this.locale).transform(date, 'dd/MM/yyyy');
            }
        } else {
            if (this.locale === 'en-GB') {
                return new DatePipe('orgen-GB').transform(date, 'MMM yyyy');
            } else {
                return new DatePipe(this.locale).transform(date, 'MMM yyyy');
            }
        }
    }

    toUtcDateFromTimestamp(d: Date | number | null | undefined): Date | null {
        if (!d) return null;

        // Wenn eine Zahl übergeben wird, als Timestamp behandeln
        const date = typeof d === 'number' ? new Date(d) : d;
        if (isNaN(date.getTime())) return null;

        // Alle Komponenten aus UTC holen und in einem "local-like" Date zurückgeben
        return new Date(
            date.getUTCFullYear(),
            date.getUTCMonth(),
            date.getUTCDate(),
            date.getUTCHours(),
            date.getUTCMinutes(),
            date.getUTCSeconds(),
            date.getUTCMilliseconds()
        );
    }
}
